// Render plugin for `provenance_validated` SSE events.
//
// validate_provenance is called TWICE, and — this is the part that shapes the
// whole component — the two calls are two TOOL CALLS. The host folds plugin
// state per toolCallId (frontend/src/agent/components/AgentChat.tsx
// foldPluginEvents: `byTool` is keyed on toolCallId and each bucket starts from
// plugin.init()), so the two events land in SEPARATE states and both render.
//
// An earlier version keyed state by dataset so a 'complete' result would
// supersede an 'initial' one. That could never fire in production — the reducer
// never sees both events — and its test only passed because it hand-folded them
// into one state, which the real fold never does. The result was a permanent
// "N awaiting replay" block sitting above the finished suite.
//
// So the two phases are rendered as what they are: a sequence. The 'initial'
// event renders a compact progress line, never a table of pending rows; only
// the 'complete' event renders the full suite. Nothing needs to supersede
// anything, which is the only design that survives the fold.
//
// SHAPE (#1347 item 15). Both phases draw ONE card in the conversation's own
// shape language — CARD_SX, a hairline, a pill — with a header row that is a
// real <button>, the way a tool chain's dots row is. The body behind it is a
// test-runner list, one line per cell, shared by both phases: an admin-screen
// MUI Table dropped into a conversation was what this looked like before.
import React from 'react'
import type { RenderPlugin, PluginAgentSSEEvent } from '../agent'
import { CARD_SX, HAIRLINE, PILL_RADIUS } from '../agent'
import { Box, Collapse, Typography } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'

export interface CellResult {
  row: number
  col: number
  rowLabel: string
  colLabel: string
  kind: 'direct' | 'derived'
  expected: number | null
  actual: number | null
  status: 'pass' | 'fail' | 'skipped'
  detail?: string
}

interface ValidationEntry {
  dataset: string
  phase: 'initial' | 'complete'
  results: CellResult[]
}

/** One bucket = one tool call = one event, so at most one entry. Keyed by
 *  dataset anyway, so a single call reporting several datasets still works. */
type ValidationPluginState = Map<string, ValidationEntry>

/** A cell awaiting the agent's replay is not the same as one that could not be
 *  checked, and must not read as one. */
const isPending = (r: CellResult) => r.status === 'skipped' && r.detail === 'awaiting replay'

const num = (v: number | null) => (v === null ? '—' : String(Number(v.toFixed(6))))

/** The dataset name and the replay note: quiet pills, the header pills' shape,
 *  no fill. A filled chip in here reads as a status; these are labels. */
const QUIET_PILL_SX = {
  display: 'inline-flex',
  alignItems: 'center',
  px: 1,
  py: '1px',
  border: HAIRLINE,
  borderRadius: PILL_RADIUS,
  backgroundColor: 'transparent',
  color: 'text.secondary',
  fontSize: 11,
  fontWeight: 500,
  lineHeight: 1.7,
  whiteSpace: 'nowrap',
  flexShrink: 0,
} as const

/** "12 passed · 1 failed · 2 skipped", with the zeroes left out — a count of
 *  nothing is not a fact worth a word. Cells awaiting replay are deliberately
 *  absent: the headline and the replay pill already say so. */
export function countsStrip(results: CellResult[]): string {
  const passed = results.filter((r) => r.status === 'pass').length
  const failed = results.filter((r) => r.status === 'fail').length
  const skipped = results.filter((r) => r.status === 'skipped' && !isPending(r)).length
  return [
    passed > 0 ? `${passed} passed` : '',
    failed > 0 ? `${failed} failed` : '',
    skipped > 0 ? `${skipped} skipped` : '',
  ].filter(Boolean).join(' · ')
}

/** A test-runner style list: one line per cell, ✓ / ✕ / … then the cell and
 *  what was checked. The ONE body both phases render — the complete phase used
 *  to draw a second, table-shaped version of the same facts.
 *
 *  Failures come first. One red line at the bottom of ninety green ones is a
 *  failure the reader has to go looking for. */
const TestLines: React.FC<{ results: CellResult[] }> = ({ results }) => {
  const ordered = [...results.filter((r) => r.status === 'fail'), ...results.filter((r) => r.status !== 'fail')]
  return (
    <Box
      component="ul"
      aria-label="Cell checks"
      sx={{ listStyle: 'none', m: 0, px: 1.5, py: 1, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12, lineHeight: 1.8 }}
    >
      {ordered.map((r) => {
        const pendingRow = isPending(r)
        const mark = r.status === 'pass' ? '✓' : r.status === 'fail' ? '✕' : pendingRow ? '…' : '?'
        const title = r.status === 'pass' ? 'passed' : r.status === 'fail' ? 'failed' : pendingRow ? 'awaiting replay' : 'skipped'
        const color = r.status === 'pass' ? 'success.main' : r.status === 'fail' ? 'error.main' : pendingRow ? 'info.main' : 'warning.main'
        const note = r.status === 'pass'
          ? `${num(r.actual)} ${r.kind === 'derived' ? 'recomputed' : 'matches source'}`
          : r.status === 'fail' ? `expected ${num(r.expected)}, got ${num(r.actual)}${r.detail ? ` — ${r.detail}` : ''}`
            : pendingRow ? 'awaiting replay' : r.detail || 'skipped'
        return (
          <Box component="li" key={`${r.row},${r.col}`} data-status={title} sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
            <Box component="span" title={title} sx={{ color, fontWeight: 700, width: 14, flexShrink: 0, textAlign: 'center' }}>{mark}</Box>
            <Box component="span" sx={{ color: 'text.primary' }}>{r.rowLabel} › {r.colLabel}</Box>
            <Box component="span" sx={{ color: 'text.secondary' }}>{r.kind}</Box>
            <Box
              component="span"
              sx={{ color: r.status === 'fail' ? 'error.main' : 'text.secondary', ml: 'auto', pl: 2, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
            >
              {note}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

/** Tick, cross, hourglass or question, in the severity's own colour. Decorative:
 *  the headline beside it says the same thing in words. */
const SeverityGlyph: React.FC<{ severity: Severity }> = ({ severity }) => {
  const sx = { fontSize: 16, flexShrink: 0 } as const
  if (severity === 'error') return <ErrorIcon color="error" sx={sx} />
  if (severity === 'warning') return <HelpOutlineIcon color="warning" sx={sx} />
  if (severity === 'info') return <HourglassEmptyIcon color="info" sx={sx} />
  return <CheckCircleIcon color="success" sx={sx} />
}

type Severity = 'error' | 'warning' | 'info' | 'success'

export const ValidationSuite: React.FC<{ entry: ValidationEntry }> = ({ entry }) => {
  const { dataset, phase, results } = entry

  // A cell still awaiting replay cannot appear in a 'complete' result — the
  // second call folds every replay in — but a malformed one could, so it is
  // still distinguished rather than silently counted as a skip.
  const pending = results.filter(isPending)
  const passed = results.filter((r) => r.status === 'pass')
  const failed = results.filter((r) => r.status === 'fail')
  const skipped = results.filter((r) => r.status === 'skipped' && !isPending(r))

  // A single failure must be impossible to miss, so severity is driven by the
  // failures alone — one red row in a hundred green ones still makes the whole
  // block read as failed. The initial call is a checkpoint, never a verdict, so
  // its non-failing state is 'info' whatever the counts say.
  const severity: Severity = phase === 'initial'
    ? (failed.length > 0 ? 'error' : 'info')
    : failed.length > 0 ? 'error' : skipped.length > 0 ? 'warning' : pending.length > 0 ? 'info' : 'success'

  const headline = phase === 'initial'
    ? (failed.length > 0
        ? `${failed.length} of ${results.length} cells do not match their source`
        : `${passed.length} cell${passed.length === 1 ? '' : 's'} checked`)
    : failed.length > 0
      ? `${failed.length} of ${results.length} cells do not match their source`
      : pending.length > 0
        ? `${passed.length} of ${results.length} cells checked — ${pending.length} awaiting replay`
        : skipped.length > 0
          ? `${passed.length} passed, ${skipped.length} could not be checked`
          : `All ${results.length} cells match their source`

  // Green runs stay out of the way; anything that failed is open on arrival.
  // Nothing later reopens it — a reader who closed it closed it.
  const [open, setOpen] = React.useState(failed.length > 0)
  const counts = countsStrip(results)

  return (
    <Box data-testid="provenance-checks" data-severity={severity} data-phase={phase} sx={{ ...CARD_SX, my: 1, fontSize: 13 }}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="provenance-checks-header"
        sx={{
          width: '100%',
          p: '8px 12px',
          backgroundColor: 'transparent',
          border: 'none',
          font: 'inherit',
          color: 'inherit',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          rowGap: '4px',
          userSelect: 'none',
          '&:hover': { backgroundColor: 'action.hover' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'info.main', outlineOffset: '-2px' },
        }}
      >
        <SeverityGlyph severity={severity} />
        <Typography component="span" variant="subtitle2" sx={{ color: severity === 'error' ? 'error.main' : 'text.primary', fontWeight: 600 }}>
          {headline}
        </Typography>
        <Box component="span" sx={QUIET_PILL_SX}>{dataset}</Box>
        {phase === 'initial' && pending.length > 0 && (
          <Box component="span" sx={QUIET_PILL_SX}>
            replaying {pending.length} derived cell{pending.length === 1 ? '' : 's'}…
          </Box>
        )}
        {counts && (
          <Typography
            component="span"
            variant="caption"
            data-testid="provenance-checks-counts"
            sx={{ color: 'text.secondary', ml: 'auto', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
          >
            {counts}
          </Typography>
        )}
        <Typography component="span" aria-hidden sx={{ color: 'text.secondary', fontSize: 13, ml: counts ? 0 : 'auto', flexShrink: 0 }}>
          {open ? '▾' : '▸'}
        </Typography>
      </Box>

      <Collapse
        in={open}
        unmountOnExit
        sx={{ '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' } }}
      >
        <Box sx={{ borderTop: HAIRLINE }}>
          <TestLines results={results} />
        </Box>
      </Collapse>
    </Box>
  )
}

export const provenanceValidationPlugin: RenderPlugin<ValidationPluginState> = {
  eventTypes: ['provenance_validated'],

  init(): ValidationPluginState {
    return new Map()
  },

  reduce(state: ValidationPluginState, event: PluginAgentSSEEvent): ValidationPluginState {
    const d = event.data
    const dataset = d.dataset as string | undefined
    if (!dataset || !Array.isArray(d.results)) return state
    const incoming: ValidationEntry = {
      dataset,
      phase: (d.phase as 'initial' | 'complete') ?? 'initial',
      results: d.results as CellResult[],
    }
    const existing = state.get(dataset)
    // Within one bucket this is belt-and-braces: a replay that delivered the
    // same dataset twice must not let an 'initial' overwrite a 'complete'.
    if (existing?.phase === 'complete' && incoming.phase === 'initial') return state
    const next = new Map(state)
    next.set(dataset, incoming)
    return next
  },

  render({ state }: { state: ValidationPluginState; toolCallId: string; sessionId: string }) {
    if (state.size === 0) return null
    return (
      <>
        {Array.from(state.values()).map((entry) => (
          <ValidationSuite key={entry.dataset} entry={entry} />
        ))}
      </>
    )
  },
}

export default provenanceValidationPlugin
